(() => {
  if (customElements.get('orvixio-variant-options')) return;

  class OrvixioVariantOptions extends HTMLElement {
    connectedCallback() {
      this.expanded = false;
      this.overflowCount = 0;
      this.layoutFrame = 0;

      this.handleClick = (event) => {
        if (!(event.target instanceof Element)) return;
        const toggle = event.target.closest('[data-more-label]');
        if (!toggle || !this.contains(toggle)) return;

        this.expanded = !this.expanded;
        this.layout();
      };

      this.handleChange = (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
        this.watchPriceChange();

        const selectedValue = this.querySelector('[data-key="orvixio-selected-value"]');
        if (selectedValue instanceof HTMLElement) {
          const nextValue = this.dataset.optionKind === 'color' ? input.value : input.value.split(' / ')[0];
          selectedValue.textContent = nextValue;

          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            selectedValue.classList.remove('is-updating');
            void selectedValue.offsetWidth;
            selectedValue.classList.add('is-updating');
            selectedValue.addEventListener('animationend', () => selectedValue.classList.remove('is-updating'), {
              once: true,
            });
          }
        }

        this.expanded = false;
        this.scheduleLayout();
      };

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      this.resizeObserver.observe(this);
      this.mutationObserver = new MutationObserver(() => this.scheduleLayout());
      this.mutationObserver.observe(this, {
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'data-current-checked', 'aria-disabled'],
      });
      this.scheduleLayout();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.priceObserver?.disconnect();
      window.clearTimeout(this.priceObserverTimer);
      cancelAnimationFrame(this.layoutFrame);
    }

    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => this.layout());
    }

    layout() {
      const list = this.querySelector('[data-option-items]');
      const toggle = this.querySelector('[data-more-label]');
      const items = Array.from(this.querySelectorAll('[data-option-item]'));
      if (!(list instanceof HTMLElement) || !(toggle instanceof HTMLElement) || items.length === 0) return;

      for (const item of items) item.classList.remove('is-overflowed');
      const fieldset = this.querySelector('.orvixio-variant-option--card');
      fieldset?.classList.toggle('is-expanded', this.expanded);

      if (this.expanded && this.overflowCount > 0) {
        toggle.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        this.updateToggleText(toggle, toggle.dataset.lessLabel || 'Show less');
        this.syncLens();
        return;
      }

      this.expanded = false;
      fieldset?.classList.remove('is-expanded');
      toggle.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');

      if (this.dataset.optionKind === 'color') this.layoutColors(items, toggle);
      else this.layoutCards(list, items, toggle);

      this.overflowCount = items.filter((item) => item.classList.contains('is-overflowed')).length;
      if (this.overflowCount === 0) {
        toggle.hidden = true;
      } else {
        toggle.hidden = false;
        const moreLabel = toggle.dataset.moreLabel || 'More options';
        this.updateToggleText(toggle, `${moreLabel} (+${this.overflowCount})`);
      }

      this.syncLens();
    }

    layoutColors(items, toggle) {
      if (items.length <= 6) return;

      for (const [index, item] of items.entries()) {
        if (index >= 6 && !item.querySelector('input:checked')) item.classList.add('is-overflowed');
      }

      while (items.filter((item) => !item.classList.contains('is-overflowed')).length > 6) {
        const candidate = [...items]
          .reverse()
          .find((item) => !item.classList.contains('is-overflowed') && !item.querySelector('input:checked'));
        if (!candidate) break;
        candidate.classList.add('is-overflowed');
      }

      toggle.hidden = false;
    }

    layoutCards(list, items, toggle) {
      const selected = items.find((item) => item.querySelector('input:checked')) || items[0];
      const listWidth = list.clientWidth;
      if (!listWidth) return;
      if (items.length <= 3) return;

      const minimumToggleWidth = 70;
      const widths = items.map((item) => Math.min(Math.max(item.scrollWidth, 58), Math.max(58, listWidth - minimumToggleWidth)));
      const totalWidth = widths.reduce((sum, width) => sum + width, 0);
      if (totalWidth <= listWidth - 4) return;

      let used = minimumToggleWidth + widths[items.indexOf(selected)];
      const keep = new Set([selected]);

      for (const [index, item] of items.entries()) {
        if (item === selected) continue;
        if (used + widths[index] <= listWidth - 4) {
          keep.add(item);
          used += widths[index];
        }
      }

      for (const item of items) {
        if (!keep.has(item)) item.classList.add('is-overflowed');
      }

      toggle.hidden = false;
    }

    syncLens() {
      if (this.dataset.optionKind === 'color') return;
      const lens = this.querySelector('[data-option-lens]');
      const selected = this.querySelector('[data-option-item]:has(input:checked)');
      const list = this.querySelector('[data-option-items]');
      if (!(lens instanceof HTMLElement) || !(selected instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

      const left = selected.offsetLeft - list.clientLeft;
      lens.style.setProperty('--orvixio-lens-x', `${left}px`);
      lens.style.setProperty('--orvixio-lens-width', `${selected.offsetWidth}px`);
      lens.classList.add('is-ready');
    }

    watchPriceChange() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const scope = this.closest('.shopify-section') || document.body;
      const priceSelector = 'product-price [ref="priceContainer"] .price__regular .price';
      const before = scope.querySelector(priceSelector)?.textContent.trim();
      this.priceObserver?.disconnect();
      window.clearTimeout(this.priceObserverTimer);

      this.priceObserver = new MutationObserver(() => {
        const price = scope.querySelector(priceSelector);
        if (!(price instanceof HTMLElement) || price.textContent.trim() === before) return;
        this.priceObserver.disconnect();
        price.animate([{ opacity: 0.42 }, { opacity: 1 }], {
          duration: 120,
          easing: 'ease-out',
        });
      });
      this.priceObserver.observe(scope, { childList: true, subtree: true, characterData: true });
      this.priceObserverTimer = window.setTimeout(() => this.priceObserver?.disconnect(), 5000);
    }

    updateToggleText(toggle, value) {
      const textNode = toggle.querySelector('[data-more-text]');
      if (textNode) textNode.textContent = value;
    }
  }

  customElements.define('orvixio-variant-options', OrvixioVariantOptions);
})();

